// @ts-nocheck
/** TODO: - Remove @ts-nocheck and add proper TypeScript types - Replace goat-style.png (1.8MB) with a compressed version or SVG icon */
import React, { Suspense, useCallback, useEffect, useLayoutEffect, useMemo, useRef, useState } from "react";
import { Outlet, useLocation } from "react-router-dom";
import { ReactComponent as ErodeMark } from "../assets/icons/erode-mark-new.svg";
import { ReactComponent as ErodeLogoBlack } from "../assets/images/erode-logo-black.svg";
import { ReactComponent as LogOutIcon } from "../assets/icons/log-out-01.svg";
import { ReactComponent as CollapseRight } from "../assets/icons/Square-Double-Alt-Arrow-Right-White.svg";
import { ReactComponent as CollapseLeft } from "../assets/icons/Square-Double-Alt-Arrow-Left-White.svg";
import ProfilePictureRounded from "../components/ProfilePictureRounded";
import { useDispatch, useSelector } from "react-redux";
import { fetchAssets } from "../store/assetsSlice";
import { fetchAssetGroups } from "../store/assetGroupSlice";
import { useNavigate } from "react-router-dom";
import { fetchAuthSession } from "aws-amplify/auth";
import { getMeUser, getMyOrganizations, performSignOut, setUserDetails, logOutUser, setTermsAndConditionsStatus, setChatPrivacyPolicyStatus, setTermsAuditTrail, setPrivacyPolicyAuditTrail, setUserConsent, authenticationState } from "../store/userSlice";
import { shouldShowConsentModal } from "../utils/termsAuditTrail";
import { loadDocuments } from "../utils/documentLoader";
import { hasTrackingConsent } from "../utils/userConsent";
// import useGetMe from "../hooks/useGetMe";
import ReactGA from "react-ga4";
import { setModalState, modalViewsConst } from "../store/modalSlice";

import { fetchOrCreateChecklist, recordLoginDate, markStepComplete } from "../store/clubChecklistSlice";

import { useChecklistSubscription } from "../hooks/useChecklistSubscription";
import { getSubscriptionStatus } from "../api/stripeSubscription";
import { usePostHog } from "@posthog/react";
import { identifyUserForPostHog } from "../utils/posthogIdentify";
import { stashReturnTo } from "../utils/marketingParams";
import { getVisibleNavLinks } from "./navConfig";
import { useCreatorMode } from "../hooks/useCreatorMode";
import CreatorModeListener from "../components/CreatorModeListener";
import { hasOrganizationAccess, isAdminUser } from "../utils/organizationMembership";
import { MobileNavProvider, MobileNavPanel, MobileNavBar } from "../components/HamburgerMenu";
import AdminErrorLogBell from "../components/ErrorLog/AdminErrorLogBell";
import LoadingIcon from "../components/LoadingIcon";
import { TourProvider, useTour } from "../components/Tour/TourProvider";
import { getTourFlag, setTourFlagStored } from "../components/Tour/tourFlags";

// Catches a lazy chunk that fails twice (offline, blocked CDN) so the route pane degrades instead of blanking the app
class RouteBoundary extends React.Component {
  state = { failed: false };

  static getDerivedStateFromError() {
    return { failed: true };
  }

  componentDidCatch(error) {
    console.error("Route failed to load", error);
  }

  // Reset on navigation instead of key={pathname} — a key remounts the routed page on every pathname change, which wipes in-component state on param navigations (e.g. AiAnalystPage's mention drafts when the URL swaps to /home/ai-analyst/:id)
  componentDidUpdate(prevProps) {
    if (this.state.failed && prevProps.pathname !== this.props.pathname) {
      this.setState({ failed: false });
    }
  }

  render() {
    if (!this.state.failed) return this.props.children;
    return (
      <div className="flex flex-col items-center justify-center h-full gap-3 p-6 text-center">
        <p className="text-sm text-gray-600">This page couldn’t be loaded. Check your connection and try again.</p>
        <button
          type="button"
          onClick={() => window.location.reload()}
          className="px-4 py-2 text-sm text-white rounded-md bg-eai-primary-accent hover:opacity-90 active:opacity-80"
        >
          Reload
        </button>
      </div>
    );
  }
}

// Navigation Item Component
interface NavItemProps {
  path: string;
  label: string;
  icon: React.ReactNode;
  isActive: boolean;
  showLabel?: boolean;
  dataTour?: string;
}

// Starts the chat tour once the welcome + consent flow is satisfied. Lives inside TourProvider so it can call useTour; navigates to the chat first so the input step's target always exists (the nav-icon step is optional and self-skips).
const ChatTourStarter = ({ ready, onStarted }: { ready: boolean; onStarted: () => void }) => {
  const { start } = useTour();
  const navigate = useNavigate();
  useEffect(() => {
    if (!ready) return; // fires once when ready flips true; onStarted clears it so it can't re-run
    navigate("/home/ai-analyst");
    start("chat");
    onStarted();
  }, [ready, navigate, start, onStarted]);
  return null;
};

const NavItem = ({ path, label, icon, isActive, showLabel = false, dataTour }: NavItemProps) => {
  const navigate = useNavigate();

  return (
    <button
      onClick={() => navigate(path)}
      aria-current={isActive ? "page" : undefined}
      data-tour={dataTour}
      title={label}
      className={`group eai-nav-item ${isActive ? "eai-nav-item-active" : ""}`}
    >
      <div className={`eai-nav-icon ${isActive ? "eai-nav-icon-active" : "group-hover:text-eai-teal-deep"}`}>
        {icon}
      </div>
      <span className={`eai-nav-label ${showLabel ? "opacity-100" : "opacity-0"}`}>
        {label}
      </span>
    </button>
  );
};

// updateCogId
const VerticalNav = () => {
  // const userHook  = useGetMe();
  const user = useSelector((state) => state.user);
  const assetGroupState = useSelector((state) => state.assetGroups);
  const location = useLocation();
  const dispatch = useDispatch();
  const navigate = useNavigate();
  const posthog = usePostHog();

  const hasRedirectedToAnalyst = useRef(false);
  const pageShellRef = useRef(null);

  const [menuExpand, setMenuExpand] = useState(false);
  const [isSmallScreen, setIsSmallScreen] = useState(false);
  const [userLogged, setUserLogged] = useState(false);
  const [isExpanded, setIsExpanded] = useState(true);
  const [signingOut, setSigningOut] = useState(false);

  const handleSignOut = async () => {
    if (signingOut) return; // guard the double-click; the reload tears the component down anyway
    try {
      setSigningOut(true);
      await performSignOut({ posthog });
    } catch (error) {
      console.error("Error signing out", error);
      setSigningOut(false);
    }
  };

  // First-run new-user tour sequencing (consent → Welcome modal → chat tour).
  const [welcomeShownState, setWelcomeShownState] = useState(false);
  const [startTourRequested, setStartTourRequested] = useState(false);
  const [docsLoaded, setDocsLoaded] = useState(false);
  const [chatTourStarted, setChatTourStarted] = useState(false);
  // Stable identity so ChatTourStarter's effect can list it as a dep without re-firing.
  const handleChatTourStarted = useCallback(() => setChatTourStarted(true), []);

  const fetchData = async () => {
    await dispatch(fetchAssets());
    await dispatch(fetchAssetGroups());
  };

  // Identify user in PostHog once permission details load (for returning users with existing consent)
  useEffect(() => {
    if (user?.permissionDetails?.id && !userLogged) {
      setUserLogged(true);
      const details = user?.details;
      if (details && hasTrackingConsent(user.permissionDetails.userConsent)) {
        identifyUserForPostHog(posthog, user.permissionDetails.id, {
          email: details.email,
          name: details.username,
          isAdmin: details.groups?.includes("Admin") ?? false,
          userConsentRaw: user.permissionDetails.userConsent,
        });
      }
    }
  }, [user?.permissionDetails?.id, user?.permissionDetails?.userConsent, user?.details, userLogged, posthog]);

  // TODO: Remove ReactGA and integrate PostHog for pageview tracking.
  useEffect(() => {
    ReactGA.send({ hitType: "pageview", page: location.pathname });
  }, [location]);

  // The shell scrolls, not the window, and it outlives the Outlet — so a new page inherits the old page's scrollTop
  useLayoutEffect(() => {
    pageShellRef.current?.scrollTo({ top: 0, left: 0 });
  }, [location.pathname]); // pathname only — a query-string filter change must not yank the user to the top

  useEffect(() => {
    // Function to check screen width
    const checkScreenSize = () => {
      setIsSmallScreen(window.innerWidth < 640); // Matches Tailwind's sm breakpoint so the sidebar and hamburger menu never show together
    };

    // Check screen size on component mount and on window resize
    checkScreenSize();
    window.addEventListener("resize", checkScreenSize);

    // Cleanup the event listener on component unmount
    return () => window.removeEventListener("resize", checkScreenSize);
  }, []);

  // Logged out on a /home deep link (e.g. a report link from a PDF): remember it so login returns here, then go to login
  const sendToLogin = () => {
    stashReturnTo(location.pathname + location.search);
    navigate("/");
  };

  const fetchUserDetails = async () => {
    try {
      const authSession = await fetchAuthSession();

      if (!authSession.tokens) {
        dispatch(logOutUser());
        sendToLogin();
      } else {
        const payload = authSession?.tokens?.idToken?.payload;
        const details = {
          groups: payload?.["cognito:groups"],
          email: payload?.email,
          username: payload?.preferred_username,
        };

        // Maybe put the two together?
        dispatch(setUserDetails(details));
        dispatch(getMeUser(authSession.userSub)); //Remove later

        // TODO: Extract auth provider parsing to a shared utility (duplicated in Authentication.tsx)
        const name = payload?.name ?? details.username;
        let authProvider = "cognito";
        const identities = payload?.identities;
        if (identities) {
          try {
            const arr = typeof identities === "string" ? JSON.parse(identities) : identities;
            if (Array.isArray(arr) && arr[0]?.providerName) authProvider = arr[0].providerName;
          } catch {
            // keep cognito
          }
        }
        if (hasTrackingConsent(user?.permissionDetails?.userConsent)) {
          identifyUserForPostHog(posthog, authSession.userSub, {
            email: details.email,
            name,
            isAdmin: details.groups?.includes("Admin") ?? false,
            authProvider,
            userConsentRaw: user?.permissionDetails?.userConsent,
          });
        }
      }
    } catch (err) {
      sendToLogin();
    }
  };

  //if use is not authenticated nav Home // CRAIG ADD SIMILAR TO HOME SCREEN
  useEffect(() => {
    fetchUserDetails();
  }, []);

  //Load Assets
  useEffect(() => {
    //get All User Assets
    fetchData();
  }, []);

  // Redirect users with no real Cognito group (non-org) to AI Analyst on initial load. Users in any org group (and any Admin group) stay on the home screen; the auto-generated Google IdP group does not count. We check the LIVE pathname (not a pathname captured at mount): VerticalNav is the layout for every /home/* route and mounts before auth resolves, so a ref snapshot was unreliable — it could capture a sub-route or a pre-auth path and then never match "/home", silently skipping the redirect. The hasRedirectedToAnalyst ref still guarantees this fires at most once, preserving "initial load only" semantics.
  useEffect(() => {
    if (hasRedirectedToAnalyst.current) return;
    if (user?.status !== authenticationState.authenticated) return;

    // Org members and admins have access; the auto-assigned "<userPoolId>_Google" group does not count (see utils/organizationMembership).
    if (!hasOrganizationAccess(user?.details?.groups) && location.pathname === "/home") {
      hasRedirectedToAnalyst.current = true;
      navigate("/home/ai-analyst", { replace: true });
    }
  }, [user?.status, user?.details?.groups, location.pathname, navigate]);

  // get user organizations
  useEffect(() => {
    if(!user?.organizations?.loaded) {
      dispatch(getMyOrganizations())
    }
  },[])

  // Load checklist and record today's login date for step 5 tracking
  useEffect(() => {
    if (user?.permissionDetails?.id) {
      dispatch(fetchOrCreateChecklist(user.permissionDetails.id)).then((result) => {
        dispatch(recordLoginDate());

        // Backfill step 6 for existing subscribers whose checklist was created before this step existed
        const checklist = result.payload;
        if (checklist && !checklist.step6Complete) {
          getSubscriptionStatus().then((sub) => {
            if (sub.status === 'active') {
              dispatch(markStepComplete(6));
            }
          }).catch(() => {}); // Silently ignore — not critical
        }
      });
    }
  }, [user?.permissionDetails?.id]);

  // Keep checklist state in sync across tabs/sessions via real-time subscription
  useChecklistSubscription();
  

  // First-run tour: read the per-user welcomeShown flag once the user is known.
  useEffect(() => {
    const id = user?.permissionDetails?.id;
    if (id) setWelcomeShownState(getTourFlag(id, "welcomeShown"));
  }, [user?.permissionDetails?.id]);

  // Consent-cleared gate: true until docs load and terms/privacy no longer need accepting.
  const termsAuditTrail = user?.permissionDetails?.termsAndConditionsAuditTrail;
  const privacyAuditTrail = user?.permissionDetails?.privacyPolicyAuditTrail;
  const consentStillNeeded = useMemo(
    () => !docsLoaded || shouldShowConsentModal(termsAuditTrail, privacyAuditTrail).show,
    [docsLoaded, termsAuditTrail, privacyAuditTrail]
  );

  // First-run tour: show the Welcome modal after consent clears, for users who haven't seen it. Read the flag directly (not just welcomeShownState) so returning users never get the modal flashed before the init effect's setState lands.
  useEffect(() => {
    const id = user?.permissionDetails?.id;
    if (!id || welcomeShownState || getTourFlag(id, "welcomeShown")) return;
    if (consentStillNeeded) return; // consent modal must be satisfied first
    dispatch(
      setModalState({
        modalOpen: true,
        modalView: modalViewsConst.welcomeTour,
        properties: { __modalSize: "md" },
      })
    );
  }, [user?.permissionDetails?.id, welcomeShownState, consentStillNeeded, dispatch]);

  // Check if user has accepted the current Terms of Service and Privacy Policy. Shows first in the first-run sequence — the Welcome modal is gated on this clearing.
  useEffect(() => {
    if (!user?.permissionDetails?.id) return;

    let cancelled = false;
    loadDocuments().then(() => {
      if (cancelled) return;
      setDocsLoaded(true);
      const consent = shouldShowConsentModal(
        user.permissionDetails?.termsAndConditionsAuditTrail,
        user.permissionDetails?.privacyPolicyAuditTrail
      );
      if (consent.show) {
        dispatch(
          setModalState({
            modalOpen: true,
            modalView: modalViewsConst.platformConsent,
            properties: {
              __modalSize: "lg",
              userId: user.permissionDetails.id,
            }
          })
        );
      }
    });

    return () => { cancelled = true; };
  }, [user?.permissionDetails?.id, user?.permissionDetails?.termsAndConditionsAuditTrail, user?.permissionDetails?.privacyPolicyAuditTrail]);
  
  // Handle modal result for platform consent (Terms of Service + Privacy Policy)
  const modalResult = useSelector((state) => state.modalState.lastResult);

  useEffect(() => {
    if (!modalResult || modalResult.modalType !== "platformConsent") return;

    if (modalResult.declined && modalResult.loggedOut) {
      // The signOut and redirect are handled in the modal component
      return;
    }

    if (modalResult.success) {
      // Update audit trails and consent in Redux immediately
      if (modalResult.termsAuditTrailJson) {
        dispatch(setTermsAuditTrail(modalResult.termsAuditTrailJson));
      }
      if (modalResult.privacyAuditTrailJson) {
        dispatch(setPrivacyPolicyAuditTrail(modalResult.privacyAuditTrailJson));
      }
      if (modalResult.userConsentJson) {
        dispatch(setUserConsent(modalResult.userConsentJson));
      }
      // Legacy fields
      if (modalResult.termsValue) {
        dispatch(setTermsAndConditionsStatus(modalResult.termsValue));
      }
      if (modalResult.chatPrivacyValue) {
        dispatch(setChatPrivacyPolicyStatus(modalResult.chatPrivacyValue));
      }

      // If user consented to tracking, identify them in PostHog now
      if (modalResult.userConsentJson && hasTrackingConsent(modalResult.userConsentJson)) {
        const details = user?.details;
        const sub = user?.permissionDetails?.id;
        if (sub && posthog) {
          identifyUserForPostHog(posthog, sub, {
            email: details?.email,
            name: details?.username,
            isAdmin: details?.groups?.includes("Admin") ?? false,
            userConsentRaw: modalResult.userConsentJson,
          });
        }
      }

      if (user?.permissionDetails?.id) {
        dispatch(getMeUser(user.permissionDetails.id));
      }
    }
  }, [modalResult, user?.permissionDetails?.id]);

  // First-run tour: record that the Welcome modal was seen; remember whether the user opted into the tour ("Show me around") so we can start it once consent is satisfied.
  useEffect(() => {
    if (!modalResult || modalResult.modalType !== "welcomeTour") return;
    const id = user?.permissionDetails?.id;
    setTourFlagStored(id, "welcomeShown");
    setWelcomeShownState(true);
    setStartTourRequested(Boolean(modalResult.startTour));
  }, [modalResult, user?.permissionDetails?.id]);

  const isAdmin = isAdminUser(user?.details?.groups);
  const subscriptionsEnabled = process.env.REACT_APP_ENABLE_SUBSCRIPTIONS === "true";
  const { canCreate } = useCreatorMode();

  // Memoized so the tour flag isn't re-read on every render (only per user).
  const chatTourCompleted = useMemo(
    () => getTourFlag(user?.permissionDetails?.id, "chatTourCompleted"),
    [user?.permissionDetails?.id]
  );

  // Chat tour is ready once the welcome is acknowledged with "Show me around", docs are loaded, consent is no longer required, and the tour hasn't run/started already.
  const chatTourReady =
    welcomeShownState &&
    startTourRequested &&
    !consentStillNeeded &&
    !chatTourStarted &&
    !chatTourCompleted;

  // Hide sidebar entirely on mobile — the shell's top bar carries the hamburger instead.
  const showNavbar = !isSmallScreen;

  return (
    <TourProvider>
    <ChatTourStarter ready={chatTourReady} onStarted={handleChatTourStarted} />
    <CreatorModeListener />
    <MobileNavProvider>
    <div className="fixed inset-0 flex w-full overflow-hidden bg-gradient-to-b from-eai-primary-accent/10 via-white to-white">
      <div className="flex w-full h-full flex-1"> {/* Container for sidebar and main content. */ }
        <div // Main div for sidebar. it includes expanded and collapsed states.
          onMouseEnter={() => setMenuExpand(true)}
          className={`z-50 ${isExpanded ? "w-56" : "w-16"} h-full ${
            showNavbar ? "flex" : "hidden"
          } flex-col pb-6 pt-4 px-3 flex-shrink-0 gap-3 border-r border-eai-border transition-all duration-300 ease-in-out`}
        >
          <div className="flex items-center justify-start overflow-visible"> {/* Header: overflow-visible so centered mark can extend past w-5 alignment column */}
            <button
              type="button"
              onClick={() => navigate("/home")}
              title="Home"
              className="w-full p-3 flex items-center justify-start relative overflow-visible"
            >
              {/* w-5 h-5 column matches NavItem; the mark fills it exactly so its left edge lines up with the nav icons */}
              <div
                className={`relative flex-shrink-0 overflow-visible ${
                  isExpanded ? "h-7 min-h-[28px] w-full" : "h-4 w-4"
                }`}
              >
                {/* Larger than the 16px nav icons — it is the brand mark, not another nav row. Absolutely centred, so growing it keeps its centre on the icon column's axis; the wrapper's overflow-visible is what lets it spill past the w-4 box. */}
                <ErodeMark
                  className={`absolute left-1/2 top-1/2 h-6 w-6 -translate-x-1/2 -translate-y-1/2 transition-opacity duration-300 ${
                    isExpanded ? "pointer-events-none opacity-0" : "opacity-100"
                  }`}
                />
                <ErodeLogoBlack
                  className={`h-7 w-auto absolute left-0 top-1/2 -translate-y-1/2 transition-opacity duration-300 ${
                    isExpanded ? "opacity-100" : "pointer-events-none opacity-0"
                  }`}
                />
              </div>
            </button>
          </div>

          {/* -my-1.5 cancels one of the two gap-3 rows this zero-height child would otherwise claim, leaving a single 12px gap with the button centred in it */}
          <div className="relative -my-1.5"> {/* Expand/collapse button */ }
            <button
              type="button"
              className="absolute -right-3 top-1/2 -translate-y-1/2 translate-x-1/2 h-7 w-7 flex items-center justify-center z-[60]"
              title={isExpanded ? "Collapse" : "Expand"}
              onClick={() => setIsExpanded((prev) => !prev)}
            >
              {isExpanded ? <CollapseRight className="h-5 w-5" /> : <CollapseLeft className="h-5 w-5" />}
            </button>
          </div>

          <div className="flex flex-col gap-0.5 items-stretch overflow-hidden"> {/* Navigation items container */ }
            {getVisibleNavLinks(isAdmin, subscriptionsEnabled, canCreate).map((link) => (
              <NavItem
                key={link.path}
                path={link.path}
                label={link.label}
                icon={<link.Icon className={`h-4 w-4 ${link.sidebarIconClassName || ""}`} />}
                isActive={link.isActive(location.pathname)}
                showLabel={isExpanded}
                dataTour={link.path === "/home/ai-analyst" ? "nav-ai-analyst" : undefined}
              />
            ))}

            {isAdmin && <AdminErrorLogBell showLabel={isExpanded} />}

          </div>

          {/* Profile + sign out — pinned to the bottom, mirroring the admin app's rail footer */}
          <div className={`mt-auto flex items-center gap-3 overflow-hidden border-t border-eai-border pt-3 ${isExpanded ? "pr-3" : "justify-center"}`}>
            {/* Avatar + name open the profile with the nav-item pill hover; px-3 lives here (not the footer) so the avatar stays on the icon column, p-1 collapsed is a 40px circle */}
            <button
              type="button"
              onClick={() => navigate("/home/settings")}
              title={user?.permissionDetails?.name || "User"}
              aria-label="Open your profile"
              className={`group/profile flex min-w-0 items-center gap-3 rounded-full text-left transition-colors hover:bg-eai-surface active:bg-eai-surface-active ${isExpanded ? "flex-1 px-3 py-1" : "p-1"}`}
            >
              {/* 32px like the admin rail — the old 44px tile left the name only ~58px before truncating */}
              <ProfilePictureRounded
                pictureURL={user?.permissionDetails?.profilePictureUrl}
                diameter={32}
                className="flex-shrink-0"
                borderClassName="border border-eai-border transition-colors group-hover/profile:border-eai-border-hover"
              />
              <span className={`min-w-0 flex-1 flex-col whitespace-nowrap transition-opacity duration-300 ${isExpanded ? "flex opacity-100" : "hidden opacity-0"}`}>
                <span className="font-outfit text-sm font-normal text-eai-ink-soft truncate transition-colors group-hover/profile:text-eai-ink">
                  {user?.permissionDetails?.name || "User"}
                </span>
                <span className="font-outfit text-xs text-eai-ink-faint">{isAdmin ? "Admin" : "User"}</span>
              </span>
            </button>
            {/* Hidden rather than unmounted when collapsed, so the 44px rail keeps the avatar centred */}
            <button
              type="button"
              onClick={handleSignOut}
              disabled={signingOut}
              title="Sign out"
              aria-label="Sign out"
              className={`eai-nav-signout ${isExpanded ? "" : "hidden"}`}
            >
              {signingOut ? (
                // Same feedback as the settings page's "Signing Out…" state, at icon size
                <svg className="h-[15px] w-[15px] animate-spin" viewBox="0 0 24 24" fill="none" aria-hidden="true">
                  <circle className="opacity-25" cx="12" cy="12" r="10" stroke="currentColor" strokeWidth="4" />
                  <path className="opacity-75" fill="currentColor" d="M4 12a8 8 0 018-8V0C5.373 0 0 5.373 0 12h4z" />
                </svg>
              ) : (
                <LogOutIcon aria-hidden className="h-[15px] w-[15px]" />
              )}
            </button>
          </div>
        </div>
        {/* min-w-0: a flex item won't shrink below its content's min width by default, so one wide table used to push this pane past the rail and change the page measure */}
        <div className="relative w-full h-full min-w-0">
          <div className="eai-app-bg w-full h-full flex flex-col">
            {/* Mobile top bar + its menu — the rail is hidden under sm, so this carries the mark and the menu trigger. Outside the scroll area so it stays put while the page moves under it, and outside the Outlet so the menu survives navigation. */}
            <MobileNavBar />
            <MobileNavPanel />
            <div ref={pageShellRef} className="eai-page-shell flex-1 min-h-0">
              {/* pathname prop (not key) so the boundary resets on navigation without remounting healthy pages */}
              <RouteBoundary pathname={location.pathname}>
                <Suspense
                  fallback={
                    <div className="flex items-center justify-center h-full" role="status" aria-label="Loading page">
                      <LoadingIcon pixelSize={80} borderThick={8} fullPage={false} />
                    </div>
                  }
                >
                  <Outlet />
                </Suspense>
              </RouteBoundary>
            </div>
          </div>
        </div>
      </div>
    </div>
    </MobileNavProvider>
    </TourProvider>
  );
};

export default VerticalNav;